import axios from 'axios';
import { axiosInstance } from '../clients/axiosClient';
import { API_V1_PREFIX } from '../utils/constants';

const CMS_ADMIN_PREFIX = `${API_V1_PREFIX}cms/admin`;

export type CmsBlock = {
  id: string;
  type: 'heading' | 'paragraph' | 'list' | 'package_card';
  level?: 2 | 3;
  text?: string;
  items?: string[];
  package_key?: string;
};

export type CmsDocument = {
  schema_version: 1;
  blocks: CmsBlock[];
};

export type CmsRevision = {
  id: number;
  revision_number: number;
  change_kind: string;
  change_note: string | null;
  changed_by: string | null;
  title: string;
  workflow_status: string;
  created_at: string;
};

export type CmsPageDetail = {
  slug: string;
  title: string;
  status: 'draft' | 'published';
  workflow_status: 'none' | 'draft' | 'in_review' | 'changes_requested';
  meta_title: string;
  meta_description: string;
  content: CmsDocument;
  return_reason: string | null;
  published_at: string | null;
  actions: string[];
  can_restore: boolean;
  live_differs: boolean;
  revisions: CmsRevision[];
};

export type CmsPageSummary = {
  slug: string;
  title: string;
  status: 'draft' | 'published';
  workflow_status: CmsPageDetail['workflow_status'];
  updated_at: string;
};

export type CmsPackage = {
  key: string;
  name: string;
  price_display: string;
  strapline: string;
  inclusions: string[];
  cta_label: string;
  cta_url: string;
  active: boolean;
  referenced_slugs: string[];
  live_slugs: string[];
};

export type CmsTransition =
  | 'start_edit'
  | 'submit'
  | 'request_changes'
  | 'publish'
  | 'unpublish'
  | 'discard';

export const cmsErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const description = error.response?.data?.description;
    if (typeof description === 'string' && description.length > 0) {
      return description;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Request failed';
};

export const listCmsPages = async (): Promise<CmsPageSummary[]> => {
  const { data } = await axiosInstance.get<{ pages: CmsPageSummary[] }>(
    `${CMS_ADMIN_PREFIX}/pages`
  );
  return data.pages;
};

export const getCmsPage = async (slug: string): Promise<CmsPageDetail> => {
  const { data } = await axiosInstance.get<CmsPageDetail>(
    `${CMS_ADMIN_PREFIX}/pages/${slug}`
  );
  return data;
};

export const createCmsPage = async (body: {
  slug: string;
  title: string;
  meta_title: string;
  meta_description: string;
  content: CmsDocument;
}): Promise<CmsPageDetail> => {
  const { data } = await axiosInstance.post<CmsPageDetail>(
    `${CMS_ADMIN_PREFIX}/pages`,
    body
  );
  return data;
};

export const saveCmsPage = async (
  slug: string,
  body: {
    title: string;
    meta_title: string;
    meta_description: string;
    content: CmsDocument;
  }
): Promise<CmsPageDetail> => {
  const { data } = await axiosInstance.put<CmsPageDetail>(
    `${CMS_ADMIN_PREFIX}/pages/${slug}`,
    body
  );
  return data;
};

export const transitionCmsPage = async (
  slug: string,
  action: CmsTransition,
  returnReason?: string
): Promise<CmsPageDetail> => {
  const { data } = await axiosInstance.post<CmsPageDetail>(
    `${CMS_ADMIN_PREFIX}/pages/${slug}/transitions`,
    { action, return_reason: returnReason ?? null }
  );
  return data;
};

export const restoreCmsRevision = async (
  slug: string,
  revisionId: number
): Promise<CmsPageDetail> => {
  const { data } = await axiosInstance.post<CmsPageDetail>(
    `${CMS_ADMIN_PREFIX}/pages/${slug}/revisions/${revisionId}/restore`
  );
  return data;
};

export const deleteCmsPage = async (slug: string): Promise<void> => {
  await axiosInstance.delete(`${CMS_ADMIN_PREFIX}/pages/${slug}`);
};

export const listCmsPackages = async (): Promise<CmsPackage[]> => {
  const { data } = await axiosInstance.get<{ packages: CmsPackage[] }>(
    `${CMS_ADMIN_PREFIX}/packages`
  );
  return data.packages;
};

export const createCmsPackage = async (
  body: Omit<CmsPackage, 'referenced_slugs' | 'live_slugs'>
): Promise<CmsPackage> => {
  const { data } = await axiosInstance.post<CmsPackage>(
    `${CMS_ADMIN_PREFIX}/packages`,
    body
  );
  return data;
};

export const updateCmsPackage = async (
  key: string,
  body: Omit<CmsPackage, 'key' | 'referenced_slugs' | 'live_slugs'>
): Promise<CmsPackage> => {
  const { data } = await axiosInstance.put<CmsPackage>(
    `${CMS_ADMIN_PREFIX}/packages/${key}`,
    body
  );
  return data;
};

export const deleteCmsPackage = async (key: string): Promise<void> => {
  await axiosInstance.delete(`${CMS_ADMIN_PREFIX}/packages/${key}`);
};

const CMS_PREFIX = `${API_V1_PREFIX}cms`;

export type CmsTopic = {
  id: number;
  title: string;
  cluster: string;
  research_doc: string;
  status: string;
  duplicate_score: number | null;
  duplicate_title: string | null;
  post_id: number | null;
};

export type CmsFlag = {
  id: number;
  block_id: string;
  code: string;
  message: string;
};

export type CmsAuthor = {
  id: number;
  name: string;
  role: string;
  credentials: string;
};

export type CmsPostDetail = {
  id: number;
  slug: string;
  title: string;
  status: string;
  workflow_status: string;
  meta_title: string;
  meta_description: string;
  json_ld_type: string;
  json_ld: Record<string, unknown> | null;
  content: CmsDocument;
  actions: string[];
  open_flags: CmsFlag[];
  reviewer: CmsAuthor | null;
  author: CmsAuthor | null;
};

export type CmsSharedItem = { key: string; value?: string; body?: string };

export type CmsAsset = {
  id: number;
  kind: string;
  filename: string;
  url: string;
  alt: string;
  credit: string;
  width: number | null;
  height: number | null;
};

export type CmsReferencePage = {
  slug: string;
  title: string;
  status: string;
};

export const listCmsTopics = async (): Promise<CmsTopic[]> => {
  const { data } = await axiosInstance.get<{ topics: CmsTopic[] }>(
    `${CMS_PREFIX}/topics`
  );
  return data.topics;
};

export const importCmsTopics = async (csv: string): Promise<CmsTopic[]> => {
  const { data } = await axiosInstance.post<{ topics: CmsTopic[] }>(
    `${CMS_PREFIX}/topics/import`,
    { csv }
  );
  return data.topics;
};

export const dismissCmsTopic = async (topicId: number): Promise<CmsTopic> => {
  const { data } = await axiosInstance.patch<CmsTopic>(
    `${CMS_PREFIX}/topics/${topicId}`,
    { dismiss_duplicate: true }
  );
  return data;
};

export const createCmsTopic = async (body: {
  title: string;
  cluster: string;
  research_doc: string;
}): Promise<CmsTopic> => {
  const { data } = await axiosInstance.post<CmsTopic>(`${CMS_PREFIX}/topics`, body);
  return data;
};

export const discardCmsTopic = async (topicId: number): Promise<void> => {
  await axiosInstance.delete(`${CMS_PREFIX}/topics/${topicId}`);
};

export const generateCmsTopic = async (
  topicId: number,
  body: {
    json_ld_type: string;
    model_key: string;
    additional_prompt: string;
    research_doc: string;
  }
): Promise<CmsPostDetail> => {
  const { data } = await axiosInstance.post<CmsPostDetail>(
    `${CMS_PREFIX}/topics/${topicId}/generate`,
    {
      system_prompt_ref: 'system',
      developer_prompt_ref: 'developer',
      json_ld_type: body.json_ld_type,
      model_key: body.model_key,
      additional_prompt: body.additional_prompt,
      research_doc: body.research_doc,
    }
  );
  return data;
};

export const listCmsPosts = async (): Promise<CmsPostDetail[]> => {
  const { data } = await axiosInstance.get<{ posts: CmsPostDetail[] }>(
    `${CMS_PREFIX}/posts`
  );
  return data.posts;
};

export const patchCmsPost = async (
  postId: number,
  body: {
    json_ld_type?: string;
    json_ld?: Record<string, unknown>;
    content?: CmsDocument;
    reviewer_id?: number;
    title?: string;
    meta_title?: string;
    meta_description?: string;
  }
): Promise<CmsPostDetail> => {
  const { data } = await axiosInstance.patch<CmsPostDetail>(
    `${CMS_PREFIX}/posts/${postId}`,
    body
  );
  return data;
};

export const transitionCmsPost = async (
  postId: number,
  action: string,
  returnReason?: string
): Promise<CmsPostDetail> => {
  const { data } = await axiosInstance.post<CmsPostDetail>(
    `${CMS_PREFIX}/posts/${postId}/transition`,
    { action, return_reason: returnReason ?? null }
  );
  return data;
};

export const clearCmsFlag = async (
  postId: number,
  flagId: number
): Promise<CmsPostDetail> => {
  const { data } = await axiosInstance.post<CmsPostDetail>(
    `${CMS_PREFIX}/posts/${postId}/flags/${flagId}/clear`
  );
  return data;
};

export const listCmsVariables = async (): Promise<CmsSharedItem[]> => {
  const { data } = await axiosInstance.get<{ variables: CmsSharedItem[] }>(
    `${CMS_PREFIX}/variables`
  );
  return data.variables;
};

export const saveCmsVariable = async (
  key: string,
  value: string
): Promise<void> => {
  await axiosInstance.post(`${CMS_PREFIX}/variables`, { key, value });
};

export const listCmsSnippets = async (): Promise<CmsSharedItem[]> => {
  const { data } = await axiosInstance.get<{ snippets: CmsSharedItem[] }>(
    `${CMS_PREFIX}/snippets`
  );
  return data.snippets;
};

export const saveCmsSnippet = async (key: string, body: string): Promise<void> => {
  await axiosInstance.post(`${CMS_PREFIX}/snippets`, { key, body });
};

export const listCmsReferences = async (
  key: string,
  kind: string
): Promise<CmsReferencePage[]> => {
  const { data } = await axiosInstance.get<{ pages: CmsReferencePage[] }>(
    `${CMS_PREFIX}/references`,
    { params: { key, kind } }
  );
  return data.pages;
};

export const listCmsAssets = async (): Promise<CmsAsset[]> => {
  const { data } = await axiosInstance.get<{ assets: CmsAsset[] }>(
    `${CMS_PREFIX}/assets`
  );
  return data.assets;
};

export const createCmsAsset = async (body: {
  kind: string;
  filename: string;
  mime_type: string;
  url: string;
  alt: string;
  credit: string;
  width: number | null;
  height: number | null;
}): Promise<CmsAsset> => {
  const { data } = await axiosInstance.post<CmsAsset>(`${CMS_PREFIX}/assets`, body);
  return data;
};

export const listCmsAuthors = async (): Promise<CmsAuthor[]> => {
  const { data } = await axiosInstance.get<{ authors: CmsAuthor[] }>(
    `${CMS_PREFIX}/authors`
  );
  return data.authors;
};

export const createCmsAuthor = async (name: string): Promise<CmsAuthor> => {
  const { data } = await axiosInstance.post<CmsAuthor>(`${CMS_PREFIX}/authors`, {
    name,
    role: 'reviewer',
    credentials: '',
  });
  return data;
};
